/**
 * Template: Solicitação de Pagamento
 *
 * Formulário para solicitar pagamentos com múltiplas notas e datas.
 * 
 * Fluxo especial:
 * - Setor 1: Abre a demanda (previsão incerta de pagamento)
 * - Setor 2: Inclui documentos (previsão incerta de pagamento)
 * - Setor 3: Efetua o pagamento (informa as datas reais)
 * 
 * O campo pagamentos (array) é editável após a criação para permitir
 * que o setor responsável adicione ou edite as notas e datas de pagamento.
 */

import type { TemplateSchema, TemplateSchemaOptions } from '../types'

/**
 * Monta o schema do template de Pagamento.
 */
export function getPagamentoSchema(_options?: TemplateSchemaOptions): TemplateSchema {
	return {
		template_id: 'solicitacao_pagamento',
		template_nome: 'Solicitação de Pagamento',
		versao: '1.0.0',
		// Bloqueia edição manual do título da demanda
		blockTitulo: true,
		// Configuração do fluxo simplificado:
		// Quando termo_colaboracao_see = true, o título da demanda é preenchido
		// automaticamente com o valor do campo tipo_pagamento.
		fluxoSimplificado: {
			ativo: false, // não oculta campos — apenas aplica a regra de título
			tituloCondicional: {
				// Campo base: sempre vira o título quando selecionado
				campoTitulo: 'tipo_pagamento',
				// Quando termo_colaboracao_see = true, adiciona sufixo ao título
				campoCondicao: 'termo_colaboracao_see',
				valorCondicao: true,
				sufixoSeCondicaoVerdadeira: ' - Termo de Colaboração SEE',
			},
		},
		// Configuração de campos editáveis após criação da demanda
		camposEditaveis: {
			pagamentos: {
				editavel: true,
				label: 'Notas e Datas de Pagamento',
				descricao: 'Adicione ou edite as notas e datas de pagamento conforme necessário',
			},
			observacoes: {
				editavel: true,
				label: 'Observações',
				descricao: 'Observações gerais sobre os pagamentos',
			},
		},
		schema: {
			type: 'object',
			title: 'Dados para solicitação de pagamento',
			description: 'Informe os dados dos pagamentos a serem realizados.',
			required: ['tipo_pagamento', 'pagamentos'],
			properties: {
				secao_dados_gerais: {
					type: 'null',
					title: 'Dados Gerais do Pagamento',
				},
				tipo_pagamento: {
					type: 'string',
					title: 'Tipo de Pagamento',
					enum: [
						'Pagamento FIEAC 01AC0001',
						'Pagamento IEL 04AC0001',
						'Pagamento SENAI 03AC0001',
						'Pagamento SENΑΙ 03AC0001',
						'Pagamento SENAI 03AC0002',
						'Pagamento SENAI 03AC0002',
						'Pagamento SENAI 03AC0003',
						'Pagamento SENΑΙ 03AC0003',
						'Pagamento SENAI 03AC0004',
						'Pagamento SENAI 03AC0004',
						'Pagamento SESI 02AC0001',
						'Pagamento SESI 02AC0003',
						'Pagamento SESI 02AC0004',
						'Pagamento SESI 02AC0005',
					],
				},
				termo_colaboracao_see: {
					type: 'boolean',
					title: 'Termo de Colaboração SEE',
					description: 'Marque se há Termo de Colaboração SEE',
					default: false,
				},
				secao_pagamento: {
					type: 'null',
					title: 'Informações dos Pagamentos',
					description: 'Adicione as notas e datas de pagamento. Você pode adicionar múltiplos itens.',
				},
				pagamentos: {
					type: 'array',
					title: 'Notas e Datas de Pagamento',
					minItems: 1,
					items: {
						type: 'object',
						required: ['nota'],
						properties: {
							nota: {
								type: 'string',
								title: 'Nota',
								minLength: 1,
								description: 'Número da nota fiscal ou identificação do pagamento',
							},
							data_pagamento: {
								type: 'string',
								title: 'Data do Pagamento',
								format: 'date',
							},
						},
					},
				},
				// secao_detalhes: {
				// 	type: 'null',
				// 	title: 'Observações Gerais',
				// },
				observacoes: {
					type: 'string',
					title: 'Observações',
					description: 'Observações gerais sobre todos os pagamentos desta solicitação.',
				},
			},
		},
		uiSchema: {
			'ui:order': [
				'secao_dados_gerais',
				'tipo_pagamento',
				'termo_colaboracao_see',
				'secao_pagamento',
				'pagamentos',
				'secao_detalhes',
				'observacoes',
			],
			secao_dados_gerais: {
				'ui:widget': 'hidden',
				'ui:options': {
					classNames: 'section-header',
				},
			},
			tipo_pagamento: {
				'ui:placeholder': 'Selecione o tipo de pagamento...',
			},
			termo_colaboracao_see: {
				'ui:widget': 'radio',
			},
			pagamentos: {
				'ui:options': {
					orderable: false,
					removable: true,
					addable: true,
				},
				items: {
					nota: {
						'ui:placeholder': 'Ex: NF-12345 ou Pagamento referente a...',
					},
					data_pagamento: {
						'ui:widget': 'date',
					},
				},
			},
			observacoes: {
				'ui:widget': 'richtext',
				'ui:placeholder': 'Observações gerais sobre os pagamentos...',
				'ui:options': {
					rows: 6,
				},
			},
			secao_pagamento: {
				'ui:widget': 'hidden',
				'ui:options': {
					classNames: 'section-header',
				},
			},
			secao_detalhes: {
				'ui:widget': 'hidden',
				'ui:options': {
					classNames: 'section-header',
				},
			},
		},
	}
}

/**
 * Instância padrão do schema.
 */
export const pagamentoSchema = getPagamentoSchema()
